import type { CanonicalParentInfo, CanonicalPatient } from '@/ordering/canonicalOrder'

import type { getPatientConsents } from './uiConfig'

type PatientConsentsSlice = ReturnType<typeof getPatientConsents>

function isNyApplicable(c: PatientConsentsSlice): boolean {
  return c.ny?.isApplicable === true
}

function isResearchApplicable(c: PatientConsentsSlice): boolean {
  return c.research?.isApplicable === true
}

/**
 * Values to store for proband secondary consents. Incidental findings is never
 * auto-seeded — it must be an explicit provider choice (the section is authored
 * `requireInteraction: true`), so `null` is preserved as the genuine
 * "untouched / unanswered" sentinel the submit gate reads. NY / research still
 * default to false only when their slot is not shown (not applicable). Never
 * changes a value the user (or a prior save) has explicitly set.
 */
export function buildProbandHiddenSecondaryConsentPatch(
  consents: PatientConsentsSlice,
  patient: CanonicalPatient | null | undefined
): Partial<
  Pick<CanonicalPatient, 'incidentalFindingsSelection' | 'newYorkSampleRetentionOptIn' | 'researchContactConsent'>
> | null {
  const p = patient ?? {}
  const patch: Partial<
    Pick<CanonicalPatient, 'incidentalFindingsSelection' | 'newYorkSampleRetentionOptIn' | 'researchContactConsent'>
  > = {}

  if (!isNyApplicable(consents) && p.newYorkSampleRetentionOptIn == null) {
    patch.newYorkSampleRetentionOptIn = false
  }
  if (!isResearchApplicable(consents) && p.researchContactConsent == null) {
    patch.researchContactConsent = false
  }

  return Object.keys(patch).length > 0 ? patch : null
}

/**
 * Comparator parent block: same policy as proband. Incidental findings
 * (`includeIncidentalFindings`) is never auto-seeded — the provider must
 * explicitly choose, so `null` survives as the "untouched" sentinel. NY /
 * research default to false only when not applicable.
 */
export function buildParentHiddenSecondaryConsentPatch(
  consents: PatientConsentsSlice,
  parent: CanonicalParentInfo | null | undefined
): Partial<
  Pick<CanonicalParentInfo, 'includeIncidentalFindings' | 'newYorkSampleRetentionOptIn' | 'researchContactConsent'>
> | null {
  const m = parent ?? {}
  const patch: Partial<
    Pick<CanonicalParentInfo, 'includeIncidentalFindings' | 'newYorkSampleRetentionOptIn' | 'researchContactConsent'>
  > = {}

  if (!isNyApplicable(consents) && m.newYorkSampleRetentionOptIn == null) {
    patch.newYorkSampleRetentionOptIn = false
  }
  if (!isResearchApplicable(consents) && m.researchContactConsent == null) {
    patch.researchContactConsent = false
  }

  return Object.keys(patch).length > 0 ? patch : null
}
